<template>
  <div v-if="notifications.length > 0" class="notification-container">
    <div 
      v-for="notification in notifications" 
      :key="notification.id"
      class="notification-banner"
      :class="[
        `notification-${notification.severity}`,
        { 'notification-dismissing': dismissingIds.has(notification.id) }
      ]"
    >
      <div class="notification-content">
        <!-- Icon based on severity -->
        <div class="notification-icon">
          <svg v-if="notification.severity === 'critical'" class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd"/>
          </svg>
          <svg v-else-if="notification.severity === 'warning'" class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd"/>
          </svg>
          <svg v-else class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd"/>
          </svg>
        </div>

        <!-- Notification text -->
        <div class="notification-text">
          <div class="notification-title">{{ notification.title }}</div>
          <div class="notification-message">{{ notification.message }}</div>
          
          <!-- Additional details (expandable) -->
          <div v-if="notification.related_data && expandedIds.has(notification.id)" class="notification-details">
            <div v-if="notification.notification_type === 'public_holiday'" class="detail-grid">
              <div><strong>Holiday:</strong> {{ notification.related_data.holiday_name }}</div>
              <div><strong>Date:</strong> {{ formatDate(notification.related_data.holiday_date) }}</div>
              <div><strong>Country:</strong> {{ notification.related_data.country }}</div>
              <div><strong>Shipment ETD:</strong> {{ formatDate(notification.related_data.etd) }}</div>
            </div>
            
            <div v-if="notification.notification_type === 'audit_due'" class="detail-grid">
              <div><strong>Last Audit:</strong> {{ formatDate(notification.related_data.last_audit_date) }}</div>
              <div><strong>Next Due:</strong> {{ formatDate(notification.related_data.next_audit_due) }}</div>
              <div><strong>Type:</strong> {{ notification.related_data.audit_type }}</div>
              <div v-if="notification.related_data.open_findings && notification.related_data.open_findings.length > 0">
                <strong>Open Items:</strong>
                <ul class="findings-list">
                  <li v-for="(finding, idx) in notification.related_data.open_findings.slice(0, 3)" :key="idx">
                    {{ finding.issue || finding.description || finding.item }}
                  </li>
                </ul>
              </div>
            </div>
            
            <div v-if="notification.notification_type === 'health_check'" class="detail-grid">
              <div><strong>Failed Count:</strong> {{ notification.related_data.failure_count }}</div>
              <div v-if="notification.related_data.recent_failures">
                <strong>Recent Failures:</strong>
                <ul class="findings-list">
                  <li v-for="(failure, idx) in notification.related_data.recent_failures.slice(0, 3)" :key="idx">
                    {{ formatDate(failure.date) }}: {{ failure.count }} animal(s) - {{ failure.reason }}
                  </li>
                </ul>
              </div>
            </div>
            
            <div v-if="notification.notification_type === 'certification_expiry'" class="detail-grid">
              <div><strong>Scheme:</strong> {{ notification.related_data.scheme }}</div>
              <div><strong>Certificate #:</strong> {{ notification.related_data.cert_number }}</div>
              <div><strong>Expires:</strong> {{ formatDate(notification.related_data.expiry_date) }}</div>
            </div>
          </div>
          
          <!-- Toggle details button -->
          <button 
            v-if="notification.related_data"
            @click="toggleExpanded(notification.id)"
            class="toggle-details-btn"
          >
            {{ expandedIds.has(notification.id) ? 'Hide details' : 'Show details' }}
          </button>
        </div>

        <!-- Dismiss button -->
        <button 
          @click="dismissNotification(notification.id)"
          class="notification-dismiss"
          title="Dismiss"
        >
          <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd"/>
          </svg>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import NotificationService from '../services/NotificationService.js';

export default {
  name: 'NotificationBanner',
  
  props: {
    notifications: {
      type: Array,
      default: () => []
    }
  },
  
  data() {
    return {
      expandedIds: new Set(),
      dismissingIds: new Set()
    };
  },
  
  methods: {
    toggleExpanded(id) {
      if (this.expandedIds.has(id)) {
        this.expandedIds.delete(id);
      } else {
        this.expandedIds.add(id);
      }
      // Force reactivity
      this.expandedIds = new Set(this.expandedIds);
    },
    
    async dismissNotification(id) {
      this.dismissingIds.add(id);
      this.dismissingIds = new Set(this.dismissingIds);
      
      const success = await NotificationService.dismissNotification(id);
      
      if (success) {
        // Emit event to parent to remove from list
        this.$emit('notification-dismissed', id);
        
        // Remove from dismissing set after animation
        setTimeout(() => {
          this.dismissingIds.delete(id);
          this.dismissingIds = new Set(this.dismissingIds);
        }, 300);
      } else {
        this.dismissingIds.delete(id);
        this.dismissingIds = new Set(this.dismissingIds);
      }
    },
    
    formatDate(dateString) {
      if (!dateString) return 'N/A';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-AU', { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric' 
      });
    }
  }
};
</script>

<style scoped>
.notification-container {
  margin-bottom: 1rem;
}

.notification-banner {
  border-radius: 0.5rem;
  padding: 1rem;
  margin-bottom: 0.75rem;
  border-left: 4px solid;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
}

.notification-banner:hover {
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
}

.notification-dismissing {
  opacity: 0;
  transform: translateX(100%);
}

.notification-critical {
  background-color: #FEF2F2;
  border-left-color: #DC2626;
}

.notification-critical .notification-icon {
  color: #DC2626;
}

.notification-warning {
  background-color: #FFFBEB;
  border-left-color: #F59E0B;
}

.notification-warning .notification-icon {
  color: #F59E0B;
}

.notification-info {
  background-color: #EFF6FF;
  border-left-color: #3B82F6;
}

.notification-info .notification-icon {
  color: #3B82F6;
}

.notification-content {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
}

.notification-icon {
  flex-shrink: 0;
  margin-top: 0.125rem;
}

.notification-text {
  flex: 1;
}

.notification-title {
  font-weight: 600;
  font-size: 0.875rem;
  color: #1F2937;
  margin-bottom: 0.25rem;
}

.notification-message {
  font-size: 0.875rem;
  color: #4B5563;
  line-height: 1.5;
}

.notification-details {
  margin-top: 0.75rem;
  padding-top: 0.75rem;
  border-top: 1px solid rgba(0, 0, 0, 0.1);
  font-size: 0.8125rem;
}

.detail-grid {
  display: grid;
  gap: 0.5rem;
}

.detail-grid > div {
  color: #374151;
}

.findings-list {
  margin-top: 0.25rem;
  margin-left: 1.25rem;
  list-style-type: disc;
}

.findings-list li {
  margin-bottom: 0.25rem;
  color: #6B7280;
}

.toggle-details-btn {
  margin-top: 0.5rem;
  font-size: 0.75rem;
  color: #3A895F;
  font-weight: 500;
  cursor: pointer;
  background: none;
  border: none;
  padding: 0;
  text-decoration: underline;
}

.toggle-details-btn:hover {
  color: #2D6B4A;
}

.notification-dismiss {
  flex-shrink: 0;
  color: #9CA3AF;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.25rem;
  border-radius: 0.25rem;
  transition: all 0.2s;
}

.notification-dismiss:hover {
  color: #4B5563;
  background-color: rgba(0, 0, 0, 0.05);
}
</style>
